import { formatINR } from '@/utils/format';

const QUARTER_LABEL: Record<string, string> = {
  Q1: 'Q1 (Apr-Jun)',
  Q2: 'Q2 (Jul-Sep)',
  Q3: 'Q3 (Oct-Dec)',
  Q4: 'Q4 (Jan-Mar)',
};

interface TdsSectionProps {
  available: boolean;
  isTdsDeducted: boolean;
  setIsTdsDeducted: (updater: (prev: boolean) => boolean) => void;
  tdsAmountInput: number | '';
  setTdsAmountInput: (v: number | '') => void;
  tdsSection?: string | null;
  tdsRate?: number | null;
  tdsPanAtInvoice?: string | null;
  financialYear: string;
  quarter: string;
  certificateNo: string;
  setCertificateNo: (v: string) => void;
  certificateDate: string;
  setCertificateDate: (v: string) => void;
  tdsAmountValue: number;
}

function TdsSection({
  available,
  isTdsDeducted,
  setIsTdsDeducted,
  tdsAmountInput,
  setTdsAmountInput,
  tdsSection,
  tdsRate,
  tdsPanAtInvoice,
  financialYear,
  quarter,
  certificateNo,
  setCertificateNo,
  certificateDate,
  setCertificateDate,
  tdsAmountValue,
}: TdsSectionProps) {
  if (!available) return null;

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
        TDS Section
      </p>

      <div className="flex items-center gap-3 p-3 rounded-lg border bg-indigo-50/50 border-indigo-200 mb-3">
        <button
          type="button"
          onClick={() => setIsTdsDeducted((v) => !v)}
          className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#4f46e5]/40 ${
            isTdsDeducted ? 'bg-[#4f46e5]' : 'bg-[#e2e8f0]'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 ${
              isTdsDeducted ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
        <p className="text-sm font-medium text-[#1e293b]">
          TDS Deducted from Vendor?{' '}
          <span className="font-semibold">{isTdsDeducted ? 'YES' : 'NO'}</span>
        </p>
      </div>

      {isTdsDeducted && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              TDS Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
                ₹
              </span>
              <input
                type="number"
                value={tdsAmountInput === '' ? '' : tdsAmountInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setTdsAmountInput(v === '' ? '' : Math.max(0, Number(v) || 0));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                }}
                className="w-full h-9 pl-7 pr-3 text-sm font-semibold border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Auto-calculated from the invoice — editable for rounding differences.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-400">TDS Section</p>
              <p className="text-sm font-semibold text-[#1e293b]">{tdsSection || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">TDS Rate</p>
              <p className="text-sm font-semibold text-[#1e293b]">{tdsRate ?? '—'}%</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Vendor PAN</p>
              <p className="text-sm font-semibold text-[#1e293b] font-mono">
                {tdsPanAtInvoice || 'NOT ON RECORD'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Financial Year</p>
              <p className="text-sm font-semibold text-[#1e293b]">{financialYear}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-slate-400">Quarter</p>
            <p className="text-sm font-semibold text-[#1e293b]">
              {QUARTER_LABEL[quarter] || quarter}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                Certificate No
              </label>
              <input
                type="text"
                value={certificateNo}
                onChange={(e) => setCertificateNo(e.target.value)}
                placeholder="Leave blank if not yet issued"
                className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                Certificate Date
              </label>
              <input
                type="date"
                value={certificateDate}
                onChange={(e) => setCertificateDate(e.target.value)}
                disabled={!certificateNo.trim()}
                className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] disabled:bg-slate-50 disabled:text-slate-400"
              />
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700">
            <i className="ri-information-line mt-0.5 shrink-0" />
            <span>
              TDS of {formatINR(tdsAmountValue)} will be withheld from this payment and
              recorded as deducted under section {tdsSection || '—'}, payable to the
              government for FY {financialYear}.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TdsSection;
